import { ActionFunctionArgs, json, LoaderFunctionArgs, redirect } from '@remix-run/node';
import { Link, Outlet, useLoaderData, useRouteError, useSubmit } from '@remix-run/react';
import NavBar from '~/components/navBar/NavBar';
import { TOKEN } from '~/constants/cookies';
import { PageRoutes, scheduleRouteRegex } from '~/constants/routes';
import { commitSession, getSession, logout } from '~/sessions';
import { getUserProfile } from '~/utils/getUserProfile';

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  if (scheduleRouteRegex.test(url.pathname)) return redirect(PageRoutes.TEMPLATES);
  const session = await getSession(request);
  const token = session.get(TOKEN);
  const profile = token ? await getUserProfile(token) : null;
  if (profile) {
    return json(
      {
        email: profile.email,
        isAuthenticated: true,
        name: profile.name,
        scheduleExists: Boolean(profile?.scheduleExists),
      },
      {
        headers: {
          'Set-Cookie': await commitSession(session),
        },
      }
    );
  }

  return json({
    email: '',
    isAuthenticated: false,
    name: '',
    scheduleExists: false,
  });
}

export async function action({ request }: ActionFunctionArgs) {
  const session = await getSession(request);
  const formData = await request.formData();
  const intent = formData.get('intent');
  if (intent === 'logout') return logout({ session });
}

export default function Schedule() {
  const submit = useSubmit();
  const { email, isAuthenticated, name, scheduleExists } = useLoaderData<typeof loader>();
  const handleLogout = () => {
    submit({ intent: 'logout' }, { method: 'POST' });
  };

  return (
    <div className="flex min-h-screen flex-col bg-neutral-offWhite">
      <NavBar isAuthenticated={isAuthenticated} onLogout={handleLogout} userData={{ email, name, scheduleExists }} />
      <div className="mb-24 px-4 sm:px-0">
        <Outlet />
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  console.error('<ErrorBoundary /> schedule', error);

  return (
    <h1 className="flex h-screen items-center justify-center bg-neutral-offWhite font-sans text-lg font-light">
      Something went wrong - please refresh the page or{' '}
      <Link to={PageRoutes.LOGIN} className="flex items-center justify-center pl-1 uppercase underline">
        click here
      </Link>
    </h1>
  );
}
